import { LayoutDashboard, MessageCircle } from 'lucide-react';
import { CompanyLogo } from './CompanyLogo';

const FEATURE_CHIPS = ['Automated', 'WhatsApp', 'Tracking'] as const;

function MiniDashboardCard() {
  return (
    <div className="mx-auto mt-5 w-full max-w-[220px] lg:hidden">
      <div className="rounded-xl bg-white/10 p-1 ring-1 ring-white/25 backdrop-blur-md">
        <div className="overflow-hidden rounded-[10px] bg-white/95 shadow-lg">
          <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-2.5 py-1.5">
            <LayoutDashboard className="h-3 w-3 text-[#2563EB]" />
            <span className="text-[10px] font-medium text-[#64748B]">Dashboard</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5 p-2">
            {[
              { label: 'Sent', value: '1.2k', color: 'bg-blue-500' },
              { label: 'Done', value: '96%', color: 'bg-emerald-500' },
              { label: 'Due', value: '42', color: 'bg-amber-500' },
            ].map((stat) => (
              <div key={stat.label} className="rounded-md border border-slate-100 bg-white p-1.5 text-center">
                <div className={`mx-auto mb-1 h-0.5 w-5 rounded-full ${stat.color}`} />
                <p className="text-[8px] text-[#64748B]">{stat.label}</p>
                <p className="text-[10px] font-bold text-[#0F172A]">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DesktopDashboardIllustration() {
  return (
    <div className="relative z-10 hidden shrink-0 justify-center px-6 pb-8 lg:flex lg:pb-10">
      <div className="animate-float-slow w-full max-w-sm animate-fade-in">
        <div className="max-h-[280px] overflow-hidden">
          <div className="rounded-2xl bg-white/10 p-1 ring-1 ring-white/25 backdrop-blur-xl">
            <div className="overflow-hidden rounded-[14px] bg-white/95 shadow-2xl shadow-blue-950/25">
              <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                <div className="flex gap-1.5" aria-hidden="true">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                </div>
                <div className="flex items-center gap-1.5 text-xs font-medium text-[#64748B]">
                  <LayoutDashboard className="h-3.5 w-3.5" />
                  Dashboard
                </div>
              </div>

              <div className="space-y-3 p-4">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Sent', value: '1.2k', color: 'bg-blue-500' },
                    { label: 'Delivered', value: '96%', color: 'bg-emerald-500' },
                    { label: 'Pending', value: '42', color: 'bg-amber-500' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="rounded-xl border border-slate-100 bg-white p-2.5 shadow-sm"
                    >
                      <div className={`mb-2 h-1 w-8 rounded-full ${stat.color}`} />
                      <p className="text-[10px] font-medium text-[#64748B]">{stat.label}</p>
                      <p className="text-sm font-bold text-[#0F172A]">{stat.value}</p>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#0F172A]">Recent Activity</span>
                    <MessageCircle className="h-3.5 w-3.5 text-[#2563EB]" />
                  </div>
                  <div className="space-y-2">
                    {[68, 48, 32].map((width, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="h-5 w-5 shrink-0 rounded-full bg-blue-100" />
                        <div
                          className="h-2 rounded-full bg-slate-200"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LoginBranding() {
  return (
    <section
      className="relative flex flex-col bg-gradient-to-br from-blue-600 to-blue-800 px-6 py-8 md:px-10 md:py-10 lg:h-full lg:min-h-0 lg:justify-center lg:overflow-hidden lg:px-16 lg:py-0"
      aria-label="Product overview"
    >
      {/* Floating shapes — desktop only */}
      <div
        className="pointer-events-none absolute -left-12 top-12 hidden h-48 w-48 rounded-full bg-white/10 blur-3xl lg:block"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-8 bottom-16 hidden h-40 w-40 rounded-full bg-blue-300/20 blur-2xl lg:block"
        aria-hidden="true"
      />
      <div
        className="animate-float-slow pointer-events-none absolute right-[10%] top-[14%] hidden h-16 w-16 rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur-md lg:block"
        aria-hidden="true"
      />

      {/* Main content */}
      <div className="relative z-10 flex flex-1 flex-col justify-center lg:min-h-0">
        <div className="mx-auto w-full max-w-lg animate-fade-in text-center lg:mx-0 lg:text-left">
          <div className="flex justify-center lg:justify-start">
            <CompanyLogo variant="light" />
          </div>

          <h1 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-white lg:mt-6 lg:text-5xl">
            CRM WhatsApp
            <br />
            Invoice Reminder
          </h1>

          <p className="mx-auto mt-3 line-clamp-2 max-w-sm text-sm leading-snug text-blue-100/90 md:max-w-md lg:mx-0 lg:mt-4 lg:max-w-md lg:text-lg">
            Automate invoice reminders, improve collections, and manage customer
            communications through WhatsApp.
          </p>

          <div className="mt-4 flex flex-wrap justify-center gap-2 md:mt-5 md:gap-2.5 lg:mt-6 lg:justify-start">
            {FEATURE_CHIPS.map((chip) => (
              <span
                key={chip}
                className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white ring-1 ring-white/25 backdrop-blur-sm lg:text-sm"
              >
                {chip}
              </span>
            ))}
          </div>

          <MiniDashboardCard />
        </div>
      </div>

      <DesktopDashboardIllustration />
    </section>
  );
}
